import React from 'react';

import { classnames, padding } from '@lumapps/classnames';
import { mdiHome } from '@lumapps/lumx/icons';
import {
    ColorPalette,
    ColorVariant,
    Divider,
    Icon,
    Size,
    SkeletonTypography,
    Text,
    Typography,
} from '@lumapps/lumx/react';
import { MOVE_DOCUMENT_FOLDER_STATUS } from '@lumapps/medias-document/constants';
import type { MoveDocumentNavigationState } from '@lumapps/medias-document/types';
import { MediaLibraryBaseBreadcrumb } from '@lumapps/medias-library/components/MediaLibraryBreadcrumb/MediaLibraryBaseBreadcrumb';
import { GLOBAL, useTranslate } from '@lumapps/translations';

export interface FolderPickerDialogBreadcrumbProps {
    /** The state object from the useMoveDocumentNavigation hook. */
    folderState: MoveDocumentNavigationState;
    /** Whether the current folder is the same as the initial one. */
    isInitialFolder: boolean;
    /** Callback function to navigate to the parent folder. */
    navigateToParent: () => void;
}

/**
 * Displays the breadcrumb for the folder picker, showing the current folder
 * and a button to navigate to the parent.
 *
 * @param {FolderPickerDialogBreadcrumbProps}
 * @returns FolderPickerDialogBreadcrumb
 */
export const FolderPickerDialogBreadcrumb: React.FC<FolderPickerDialogBreadcrumbProps> = ({
    folderState,
    isInitialFolder,
    navigateToParent,
}) => {
    const { translateKey, translate } = useTranslate();

    const initialFolderColorProps = !isInitialFolder ? { color: ColorPalette.blue, colorVariant: ColorVariant.N } : {};

    const renderContent = () => {
        switch (folderState.status) {
            case MOVE_DOCUMENT_FOLDER_STATUS.ROOT:
                return (
                    <Icon
                        icon={mdiHome}
                        size={Size.s}
                        aria-label={translateKey(GLOBAL.HOME)}
                        {...initialFolderColorProps}
                    />
                );

            case MOVE_DOCUMENT_FOLDER_STATUS.LOADED:
                return (
                    <Text as="p" truncate typography={Typography.subtitle1} {...initialFolderColorProps}>
                        {translate(folderState.folder.name)}
                    </Text>
                );

            case MOVE_DOCUMENT_FOLDER_STATUS.LOADING:
            default:
                return <SkeletonTypography typography={Typography.subtitle1} width="100px" />;
        }
    };

    return (
        <>
            <Divider />

            <MediaLibraryBaseBreadcrumb
                className={classnames(padding('all', Size.big))}
                folderState={folderState}
                navigateToParent={navigateToParent}
            >
                {renderContent()}
            </MediaLibraryBaseBreadcrumb>
        </>
    );
};
